"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { z } from "zod/v4";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createPasswordReset } from "@/lib/auth-demo";

const forgotPasswordSchema = z.object({
  email: z.email("Please enter a valid email address"),
});

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const result = forgotPasswordSchema.safeParse({ email });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    // Issue a reset token for the link below. A real backend would email the
    // link; here we show the same confirmation whether or not the email has an
    // account, so we never reveal which addresses are registered.
    setResetToken(createPasswordReset(email));
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-6 text-center">
        <span className="flex size-14 items-center justify-center rounded-full border border-line text-secondary">
          <Mail className="size-6" aria-hidden="true" />
        </span>
        <div>
          <h1 className="font-heading text-3xl font-semibold text-dark">
            Check your email
          </h1>
          <p className="mt-2 text-sm text-mid">
            We&apos;ve sent a password reset link to{" "}
            <span className="font-semibold text-dark">{email}</span>. It may
            take a minute or two to arrive.
          </p>
        </div>
        <p className="text-sm text-mid">
          Didn&apos;t receive it?{" "}
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="font-semibold text-primary underline-offset-2 hover:text-secondary hover:underline"
          >
            Try again
          </button>
        </p>
        {resetToken && (
          <div className="w-full rounded-md border border-secondary/40 bg-secondary/[0.06] p-4 text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-mid">
              Demo shortcut
            </p>
            <p className="mt-1 text-sm text-mid">
              This demo doesn&apos;t send real email yet. Use the link below to
              set a new password on this device.
            </p>
            <Link
              href={`/reset-password?token=${resetToken}`}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary underline-offset-2 hover:text-secondary hover:underline"
            >
              Set a new password
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        )}
        <Link
          href="/login"
          className="inline-flex items-center gap-2 text-sm text-mid transition-colors hover:text-primary"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <span className="eyebrow">Account recovery</span>
        <h1 className="mt-2 font-heading text-3xl font-semibold text-dark">
          Reset your password
        </h1>
        <p className="mt-2 text-sm text-mid">
          Enter the email address on your account and we&apos;ll send you a
          link to set a new password.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label="Email address"
          type="email"
          placeholder="you@example.com"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />

        <Button type="submit" variant="primary" className="w-full">
          Send reset link
        </Button>
      </form>

      <Link
        href="/login"
        className="inline-flex items-center justify-center gap-2 text-sm text-mid transition-colors hover:text-primary"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Back to sign in
      </Link>
    </div>
  );
}
